// src/pages/offlineSales/components/OfflineSalesPaymentRecordsModal.jsx
//
// Payment Records modal for the All Offline Sales Orders list page — the shared
// src/components/common/PaymentRecordsModal driven by the channel-agnostic
// src/services/api/paymentVerification.js endpoints (view/edit/verify), recordOrderPayment()
// (POST /offline-sales/orders/:id/payments) for new payments, and credit notes as the offset.
import React, { useMemo } from 'react';
import {
  getPaymentRecords,
  updatePaymentRecord,
  verifyPaymentRecord,
  bulkVerifyPaymentRecords,
} from '../../../services/api/paymentVerification';
import { recordOrderPayment, uploadOrderPaymentProof, getOrder } from '../../../services/api/offlineSales/orders';
import { getCreditNotes, applyCreditNote } from '../../../services/api/offlineSales/creditNotes';
import { getPaymentMethods } from '../../../services/api/offlineSales/paymentMethods';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { usePermissions } from '../../../hooks/usePermissions';
import PaymentRecordsModal from '../../../components/common/PaymentRecordsModal/PaymentRecordsModal';

const normalizeRecord = (record) => ({ ...record, paymentMethod: record.PaymentMethod });

const OfflineSalesPaymentRecordsModal = ({ order, onClose, onSaved }) => {
  const { t } = useLocalization();
  const { hasPermission } = usePermissions();
  const canVerify = hasPermission('verify_payment_records');
  const canApplyCreditNote = hasPermission('issue_credit_note');

  const adapter = useMemo(() => ({
    inputIdPrefix: 'os',
    enableChequeDetails: true,
    canEditRecords: canVerify,
    getNumber: (entity) => entity.orderNumber,
    getBalance: (entity) => parseFloat(entity.balanceAmount || 0),
    getPartyId: (entity) => entity.customerId || entity.customer?.id || entity.Customer?.id,
    loadRecords: async (orderId) => {
      const response = await getPaymentRecords({ orderId, limit: 100 });
      return (response.paymentRecords || []).map(normalizeRecord);
    },
    updateRecord: async (id, data) => normalizeRecord((await updatePaymentRecord(id, data)).paymentRecord),
    recordPayments: (orderId, payments) => recordOrderPayment(orderId, payments),
    reloadEntity: async (orderId) => {
      const freshOrder = await getOrder(orderId);
      return freshOrder.order || freshOrder;
    },
    loadMethods: getPaymentMethods,
    uploadProof: uploadOrderPaymentProof,
    verification: {
      canVerify,
      verifyRecord: async (id) => normalizeRecord((await verifyPaymentRecord(id)).paymentRecord),
      bulkVerify: bulkVerifyPaymentRecords,
    },
    offset: {
      key: 'credit_note_apply',
      label: t('offlineSales.creditNotePaymentMethod') || 'Credit Note',
      iconFileName: 'credit-note.svg',
      numberField: 'creditNoteNumber',
      canApply: canApplyCreditNote,
      loadNotes: async (customerId) => (await getCreditNotes({ customerId, limit: 200 })).creditNotes || [],
      applyNote: (noteId, orderId, amount) => applyCreditNote(noteId, { orderId, amount }),
      texts: {
        select: t('offlineSales.selectCreditNote') || 'Select Credit Note',
        search: t('offlineSales.searchCreditNote') || 'Search credit notes...',
        none: t('offlineSales.noAvailableCreditNotes') || 'No available credit notes',
        applyAmount: t('creditNote.applyAmount'),
        amountMustBePositive: t('creditNote.amountMustBePositive'),
        exceedsRemaining: t('creditNote.applyAmountExceedsRemaining'),
        exceedsBalance: t('creditNote.applyAmountExceedsOrderBalance'),
      },
    },
    texts: {
      title: t('offlineSales.paymentDetails'),
      outstandingBalance: t('offlineSales.outstandingBalance') || 'Outstanding Balance',
      amount: t('offlineSales.amount'),
      reference: t('offlineSales.reference'),
      confirmPayment: t('offlineSales.confirmPayment') || 'Save Payment',
      someFieldsRequired: t('offlineSales.someFieldsRequired') || 'Please complete all payment fields',
      paymentRecordedSuccess: t('offlineSales.paymentRecordedSuccess') || 'Payment recorded',
      failedToRecordPayment: t('offlineSales.failedToRecordPayment'),
    },
  }), [t, canVerify, canApplyCreditNote]);

  return <PaymentRecordsModal entity={order} adapter={adapter} onClose={onClose} onSaved={onSaved} />;
};

export default OfflineSalesPaymentRecordsModal;
